import React, { Fragment, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import {
  getNegativeOutcome,
  getQuestionOutcomes,
} from 'src/components/JobChecklistForm/utils';
import { getQuestionNumber } from 'src/components/ChecklistPreviewForm/utils';
import { JobChecklistFormSectionDefaultProps, JobChecklistFormSectionPropTypes } from 'src/components/JobChecklistForm/propTypes';
import { Popup } from 'src/components/Popup';
import FormRow from 'src/components/Form/FormRow';
import Button from 'src/components/Button';
import s from 'src/components/JobChecklistForm/JobChecklistForm.styles';

const JobChecklistFormSectionComponent = props => {
  const {
    applyQuestionConditions,
    checklistOutcomes,
    collapsedSections,
    getFactoryQuestion,
    getQuestionColor,
    getResult,
    getSectionQuestions,
    indexPath,
    parentChecklistSectionIdAlias,
    questionResultDrawCallback,
    questionsWithMissingResults,
    reportReviewStatistics,
    section,
    sectionHeaderAdditions,
    sectionsOptions,
    selectedQuestion,
    setShowHistory,
    setShowNote,
    setShowResult,
    toggleSection,
    toggleSelectedQuestion,
    typeId,
    wrapperRef,
    setResult,
    allOutcomes,
    allNegativeOutcomes,
    negativeOutcomeResultIds,
    allPositiveOutcomes,
    positiveOutcomeResultIds,
    allOtherOutcomes,
    allOtherOutcomeResultIds,
    checklist,
  } = props;

  const { t } = useTranslation();

  const [isSetAllResultsConfirmationActive, setIsSetAllResultsConfirmationActive] = useState(false);

  const parentIndex = `${indexPath.map(v => v + 1).join('.')}${
    indexPath.length > 0 ? '.' : ''
  }`;
  const questions = getSectionQuestions(section, applyQuestionConditions(section));

  const number =
    `${parentChecklistSectionIdAlias || parentIndex}` +
    `${section.checklistSectionIdAlias || `${section.position + 1}.`}`;

  const isCollapsedSection =
    collapsedSections.indexOf(section.originalIndex) !== -1;

  const toggleSectionQuestionsOrSubsections = (hasSubsections) => {
    if (questions.length > 0 || hasSubsections) {
      toggleSection(section.originalIndex);
    }
  };

  const statistic = reportReviewStatistics.filter(
    r => r.type === 'checklist' && +r.typeId === +typeId,
  )[0];

  const getDescendantQuestion = se => {
    let result = [...se.questions];
    result = result.concat(
      se.children.map(c => getDescendantQuestion(c).flat()).flat(),
    );
    return result;
  };

  const descendantQuestions = getDescendantQuestion(section);
  const descendantQuestionIds = descendantQuestions.map(q => q.id);

  const sectionHasRequiredAction =
    statistic &&
    statistic.actionRequiredtypeQuestionIds.some(id => descendantQuestionIds.includes(id));

  const NoteExistedComponent = (questionId, switchToNoteOfQuestion) => {
    if (
      statistic &&
      statistic.actionRequiredtypeQuestionIds.includes(questionId)
    ) {
      return (
        <s.NoteExistedIcon actionRequired onClick={switchToNoteOfQuestion}>
          {t('REVIEW_NOTES')}
        </s.NoteExistedIcon>
      );
    }
    if (
      statistic &&
      statistic.actionNotRequiredQuestionIds.includes(questionId)
    ) {
      return (
        <s.NoteExistedIcon onClick={switchToNoteOfQuestion}>
          {t('REVIEW_NOTES')}
        </s.NoteExistedIcon>
      );
    }
    return null;
  };

  const getQuestionOutcomesWithOverriden = (question) => {
    const questionOutcomes = getQuestionOutcomes(question);
    return questionOutcomes || checklistOutcomes || [];
  };

  const getAllQuestions = (sectionToParse) => {
    const sectionQuestions = getSectionQuestions(sectionToParse, applyQuestionConditions(sectionToParse));
    return [
      sectionQuestions,
      ...(sectionToParse.children || []).map(subSection => getAllQuestions(subSection)),
    ].flat(1);
  };

  const setAllResults = (resultId) => {
    getAllQuestions(section).forEach(question => {
      const resultIds = getQuestionOutcomesWithOverriden(question).map(c => c.resultId);
      if (resultIds.includes(resultId)) {
        setResult(question, 'resultId', resultId);
      }
    });
  };

  const quickResult = section.isQuickActive ? allOtherOutcomes.find(c => c.resultId === section.quickResultId) : null;
  const allNegativeOutcomesResultIds = allNegativeOutcomes.map(c => c.resultId);
  const hasNegativeOutcomeSelectedForQuick = section.isQuickActive && getAllQuestions(section).some(question => {
    return allNegativeOutcomesResultIds.includes(getResult(question).resultId);
  });

  const quickResultButton = () => {
    if (!quickResult || hasNegativeOutcomeSelectedForQuick) {
      return null;
    }

    return (
      <s.QuickBtn onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();

        setIsSetAllResultsConfirmationActive(true);
      }}>{t('SET_ALL_TO', { result: quickResult?.result?.currentVersion?.code })}</s.QuickBtn>
    );
  };

  const quickResultConfirmationPopup = () => {
    if (!isSetAllResultsConfirmationActive) {
      return null;
    }
    
    return (
      <Popup
        onClose={() => setIsSetAllResultsConfirmationActive(false)}
        title={t('SET_ALL_CONFIRMATION', { result: quickResult?.result?.currentVersion?.code })}
      >
        <FormRow spaced>
          <Button
            onClick={e => {
              e.preventDefault();

              setAllResults(quickResult?.resultId);
              setIsSetAllResultsConfirmationActive(false);
            }}
            orange
            sm
            solid
          >
            {t('OK')}
          </Button>
          <Button ghost onClick={() => setIsSetAllResultsConfirmationActive(false)} red sm type="button">
            {t('CANCEL')}
          </Button>
        </FormRow>
      </Popup>
    );
  };

  if (section.children.length > 0) {
    return (
      <s.SectionWrapper
        key={section.position}
        collapsed={isCollapsedSection}
        level2={indexPath.length > 1}
        ref={wrapperRef}
        root={indexPath.length === 0}
        >
        {quickResultConfirmationPopup()}
        {sectionHeaderAdditions
          ? sectionHeaderAdditions(section, indexPath.length === 0)
          : null}
        {sectionHasRequiredAction && <s.RedDot />}
        <s.SectionTitle onClick={() => toggleSectionQuestionsOrSubsections(true)}>
          <s.SectionNumber>{number}</s.SectionNumber>
          <s.SectionLabel>
            {section.name}
            {quickResultButton()}
            <s.SectionToggleBtn collapsed={isCollapsedSection} name="expand" />
          </s.SectionLabel>
        </s.SectionTitle>
        {!isCollapsedSection &&
          section.children.map(subSection => (
            <JobChecklistFormSection
              key={subSection.originalIndex}
              {...props}
              indexPath={[...indexPath, section.position]}
              parentChecklistSectionIdAlias={number}
              reportReviewStatistics={reportReviewStatistics}
              section={subSection}
              wrapperRef={
                (
                  sectionsOptions.find(
                    c => c.value === subSection.originalIndex,
                  ) || { ref: null }
                ).ref
              }
            />
          ))}
      </s.SectionWrapper>
    );
  }

  const onQuestionResultDraw =
    questionResultDrawCallback ||
    (question => {
      const questionResult = getResult(question);
      const questionResultOutcomeIsPositive = positiveOutcomeResultIds.includes(questionResult.resultId);
      const questionResultOutcomeIsNegative = negativeOutcomeResultIds.includes(questionResult.resultId);
      const questionResultOutcomeIsOtherType = allOtherOutcomeResultIds.includes(questionResult.resultId);

      if (questionResult && questionResultOutcomeIsPositive) {
        return (
          <Fragment>
            <s.QuestionResultCompliant>
              <s.QuestionResultIcon name="check" />
              {allPositiveOutcomes.find(outcome => outcome.resultId === questionResult.resultId)?.result.currentVersion.code}
            </s.QuestionResultCompliant>
          </Fragment>
        );
      }

      if (questionResult && questionResultOutcomeIsNegative) {
        return (
          <Fragment>
            <s.QuestionResultNonCompliant>
              <s.QuestionResultIcon name="cross" />
              {allNegativeOutcomes.find(outcome => outcome.resultId === questionResult.resultId)?.result.currentVersion.code}
            </s.QuestionResultNonCompliant>
          </Fragment>
        );
      }

      if (questionResult && questionResultOutcomeIsOtherType) {
        return (
          <s.QuestionResultNA>
            <s.QuestionResultIcon name="hourglass" />
            {allOtherOutcomes.find(outcome => outcome.resultId === questionResult.resultId)?.result.currentVersion.code}
          </s.QuestionResultNA>
        );
      }

      return null;
    });

  const getFirstHiddenWildCardQuestion = () => {
    return questions.find(question => {
      if (!question?.checklistQuestion?.currentVersion?.isWildCardQuestion) {
        return false;
      }

      return positiveOutcomeResultIds.includes(getResult(question).resultId);
    });
  };

  const sectionHasWildCardQuestions = questions.find(q => q?.checklistQuestion?.currentVersion?.isWildCardQuestion) !== undefined;
  const sectionHasHiddenWildCardQuestions = getFirstHiddenWildCardQuestion() !== undefined;

  const displayWildCardQuestion = () => {
    const firstHiddenWildCardQuestion = getFirstHiddenWildCardQuestion();
    if (!firstHiddenWildCardQuestion) {
      return;
    }

    const questionOutcomes = getQuestionOutcomes(firstHiddenWildCardQuestion);
    const outcomes = questionOutcomes || checklistOutcomes || [];
    const negativeOutcome = getNegativeOutcome(outcomes);

    setResult(firstHiddenWildCardQuestion, 'resultId', negativeOutcome.resultId);
    if (getResult(firstHiddenWildCardQuestion).resultId === negativeOutcome.resultId) {
      const newNonCompliances = [...(getResult(firstHiddenWildCardQuestion).nonCompliances || [])];
      if (newNonCompliances.length === 0) {
        newNonCompliances.push({
          id: null,
          lawId: null,
          findingType: 'new'
        });
        setResult(firstHiddenWildCardQuestion, 'nonCompliances', newNonCompliances);
      }

      toggleSelectedQuestion(section.originalIndex, firstHiddenWildCardQuestion.position, getQuestionNumber(number, firstHiddenWildCardQuestion));
      setShowResult(true);
      setShowNote(false);
    }
  };

  return (
    <s.SectionWrapper
      key={section.position}
      collapsed={isCollapsedSection}
      level2={indexPath.length > 1}
      ref={wrapperRef}
      root={indexPath.length === 0}
      >
      {quickResultConfirmationPopup()}
      {sectionHeaderAdditions
        ? sectionHeaderAdditions(section, indexPath.length === 0)
        : null}
      {sectionHasRequiredAction && <s.RedDot />}
      <s.SectionTitle onClick={() => toggleSectionQuestionsOrSubsections(false)}>
        <s.SectionNumber>{number}</s.SectionNumber>
        <s.SectionLabel>
          {section.name}
          {quickResultButton()}
          <s.SectionToggleBtn collapsed={isCollapsedSection} name="expand" />
        </s.SectionLabel>
      </s.SectionTitle>
      {!isCollapsedSection && questions.length > 0 && (
        <>
          {questions.map(question => {

            if (question.checklistQuestion.currentVersion.isWildCardQuestion
              && positiveOutcomeResultIds.includes(getResult(question).resultId)) {
              return null;
            }

            let questionText = getFactoryQuestion(question).text;
            if (question.checklistQuestion.currentVersion.isWildCardQuestion) {
              const { detailedFinding } = getResult(question) || {};
              if (detailedFinding) {
                questionText = (detailedFinding.length > 255) ? `${detailedFinding.substr(0, 255)}…` : detailedFinding;
              }
            }

            const isActionRequired = statistic && statistic.actionRequiredtypeQuestionIds.includes(question.id);
            const switchToNoteOfQuestion = e => {
              toggleSelectedQuestion(
                section.originalIndex,
                question.position,
                getQuestionNumber(number, question),
              );
              e.preventDefault();
              e.stopPropagation();
              setShowResult(false);
              setShowNote(true);
              if (setShowHistory) {
                setShowHistory(false);
              }
            };
            const hasMissingResult = !!questionsWithMissingResults.find(q => q.checklistQuestionId === question.checklistQuestionId);
            const isNegativeResultId = negativeOutcomeResultIds.includes(getResult(question).resultId);
            const isFindingsFilled = getResult(question).detailedFinding &&
                                     (!checklist?.currentVersion?.hasRecommendations || getResult(question).recommendations) &&
                                     ((!checklist?.currentVersion?.hasLegalRequirements || getResult(question).legalRequirements) || getResult(question).nonCompliances.length > 0);

            const showAlert = hasMissingResult || (isNegativeResultId && !isFindingsFilled);
            const showCheckmark = !showAlert && isNegativeResultId && isFindingsFilled;

            return (
              <Fragment key={question.id}>
                <s.QuestionWrapper
                  key={question.position}
                  color={getQuestionColor ? getQuestionColor(question) : null}
                  onClick={() => {
                    toggleSelectedQuestion(
                      section.originalIndex,
                      question.position,
                      getQuestionNumber(number, question),
                    );
                    setShowResult(true);
                    setShowNote(false);
                    if (setShowHistory) {
                      setShowHistory(false);
                    }
                  }}
                  selected={
                    selectedQuestion &&
                    selectedQuestion.section === section.originalIndex &&
                    selectedQuestion.question === question.position
                  }
                >
                  {isActionRequired && <s.RedDot />}
                  <s.QuestionNumber>
                    {getQuestionNumber(number, question)}
                  </s.QuestionNumber>
                  <s.QuestionLabel>
                    <s.QuestionLabelText>{questionText}</s.QuestionLabelText>
                    <s.QuestionResultLabel>
                      {onQuestionResultDraw(question)}
                      <s.QuestionResultLabel>
                      {getResult(question)?.correctedDate && (
                          <s.BlueLabel >{t('CORRECTED_FINDING')}</s.BlueLabel>
                        )
                      }
                      {NoteExistedComponent(question.id, e =>
                        switchToNoteOfQuestion(e),
                      )}
                      </s.QuestionResultLabel>
                    </s.QuestionResultLabel>
                    {showAlert && <s.QuestionResultDetails name="warning" red />}
                    {showCheckmark && <s.QuestionResultDetails name="checkbox" />}
                    {hasMissingResult && <s.AlertText >{t('MISSING_RESULT_ALERT')}</s.AlertText>}
                  </s.QuestionLabel>
                </s.QuestionWrapper>
              </Fragment>
            );
          })}

          {sectionHasWildCardQuestions && (
            <s.AddButtonWrapper addNonCompliance inactive={!sectionHasHiddenWildCardQuestions} onClick={() => displayWildCardQuestion()}>
              <s.AddButton name="plus" />
              <s.AddButtonLabel>{t('NON_COMPLIANCE')}</s.AddButtonLabel>
            </s.AddButtonWrapper>
          )}
        </>
      )}
    </s.SectionWrapper>
  );
};

JobChecklistFormSectionComponent.propTypes = JobChecklistFormSectionPropTypes;
JobChecklistFormSectionComponent.defaultProps = JobChecklistFormSectionDefaultProps;

export const JobChecklistFormSection = React.memo(JobChecklistFormSectionComponent);
